import { useMemo } from "react"
import { PreloadedQuery, usePreloadedQuery } from "react-relay"
import { ScrollWrapper } from "@/components/scroll-wrapper"
import { ChatMessageItem } from "@/modules/chats/components/chat-message/chat-message"
import { ChatSystemMessage } from "@/modules/chats/components/chat-message/chat-system-message"
import { mapChatMessageEdges } from "@/modules/chats/lib/map-chat-message"
import { groupMessage } from "@/modules/chats/lib/message-helper"
import { useChatMessagesQuery } from "@/modules/chats/services/__generated__/useChatMessagesQuery.graphql"
import { chatMessagesQuery } from "@/modules/chats/services/useChatMessagesQuery"
import { LiveTranslationView } from "@/modules/chats/types/chat-message"
import { ChatThread } from "@/modules/chats/types/chat-thread"

type ChatListProps = {
  queryRef: PreloadedQuery<useChatMessagesQuery>
  selectedThread: ChatThread
}

export const ChatList = (props: ChatListProps) => {
  const { queryRef, selectedThread } = props

  const { chatMessages } = usePreloadedQuery<useChatMessagesQuery>(chatMessagesQuery, queryRef)

  const liveTranslation: LiveTranslationView = {
    isLiveTranslationEnabled: selectedThread.autoTranslationSetting?.isLiveTranslationEnabled ?? false,
    selectedTargetLanguage: selectedThread.autoTranslationSetting?.targetAgentLanguage ?? null,
  }

  const messageGroup = useMemo(() => {
    const messages = mapChatMessageEdges(chatMessages?.edges ?? [])
    return groupMessage(messages)
  }, [chatMessages?.edges])

  return (
    <ScrollWrapper className="flex h-full flex-col-reverse">
      <div className="mt-auto grid gap-4 px-4 pb-6">
        {messageGroup.map((group) => {
          return (
            <div key={group.id} className="grid gap-0.5">
              {group.messages.map((message, messageIndex) => {
                if (message.role === "system") {
                  return <ChatSystemMessage key={message.id} message={message} />
                }

                return (
                  <ChatMessageItem
                    key={message.id}
                    chatMessage={message}
                    isLastMessageInGroup={messageIndex === group.messages.length - 1}
                    shouldShowAvatar={false}
                    appointmentId={selectedThread.appointmentId}
                    patient={selectedThread.patient}
                    patientLanguage={selectedThread.language}
                    liveTranslation={liveTranslation}
                  />
                )
              })}
            </div>
          )
        })}
      </div>
    </ScrollWrapper>
  )
}
